import { useState, useEffect, useRef, useId } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Home, User, Briefcase, Mail } from 'lucide-react';


const Header = () => {
  const [headerState, setHeaderState] = useState({
    isScrolled: false,
    isVisible: true
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const mobileMenuId = useId();

  useEffect(() => {
    const handleScroll = () => {
      if (!ticking?.current) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          
          // Consolidate all state updates into single setState call
          setHeaderState(prev => {
            const isScrolled = currentScrollY > 50;
            let isVisible = prev?.isVisible;
            
            if (currentScrollY < 10) {
              isVisible = true;
            } else if (currentScrollY > lastScrollY?.current && currentScrollY > 100) {
              isVisible = false;
            } else if (currentScrollY < lastScrollY?.current) {
              isVisible = true;
            }
            
            lastScrollY.current = currentScrollY;
            
            // Only update if values actually changed
            if (prev?.isScrolled === isScrolled && prev?.isVisible === isVisible) {
              return prev;
            }
            
            return { isScrolled, isVisible };
          });
          
          ticking.current = false;
        });
        ticking.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close menu when route changes (pathname is stable, so this is intentional)
  const currentPath = location.pathname;
  useEffect(() => {
    setIsMenuOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const navigationLinks = [
    {
      path: '/',
      label: 'Home',
      icon: Home,
      color: 'text-navy',
    },
    {
      path: '/about',
      label: 'About',
      icon: User,
      color: 'text-teal',
    },
    {
      path: '/partnerships',
      label: 'Partnerships',
      icon: Briefcase,
      color: 'text-sage',
    },
    {
      path: '/contact',
      label: 'Contact',
      icon: Mail,
      color: 'text-gold',
    },
  ];

  const isActivePath = (path) => {
    if (path === '/') {
      return location?.pathname === '/' || location?.pathname === '/home';
    }
    return location?.pathname === path;
  };

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-40 transition-transform duration-300 ease-in-out ${
          headerState?.isVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
        style={{
          background: headerState?.isScrolled 
            ? 'linear-gradient(to bottom, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%)'
            : 'linear-gradient(to bottom, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          boxShadow: headerState?.isScrolled ? '0 4px 20px rgba(26, 58, 82, 0.08)' : 'none',
          willChange: 'transform'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-18 lg:h-20">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-sm group z-50"
              aria-label="The Poetic Method home page"
            >
              <img
                src="/assets/images/TPM_FULL-1766717367317.png"
                alt="The Poetic Method™ logo featuring a stylized feather/quill pen in teal color with navy nib, combined with an upward trending coral-colored graph arrow"
                className={`w-auto transition-all duration-300 ${
                  headerState?.isScrolled ? 'h-12 sm:h-14 lg:h-16' : 'h-14 sm:h-16 lg:h-18'
                }`}
                style={{ willChange: 'height' }}
                width="280"
                height="72"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-xs" aria-label="Main navigation">
              {navigationLinks?.map((link) => {
                const isActive = isActivePath(link?.path);
                
                return (
                  <Link
                    key={link?.path}
                    to={link?.path}
                    className={`px-md py-sm rounded-lg text-sm sm:text-base font-body font-semibold transition-all duration-300 ${
                      isActive
                        ? 'bg-navy text-white shadow-md ring-2 ring-coral ring-offset-2'
                        : 'text-navy hover:bg-white/60 hover:shadow-sm'
                    }`}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {link?.label}
                  </Link>
                );
              })}
            </nav>

            {/* Mobile Hamburger Menu Button */}
            <button
              type="button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg transition-all duration-300 hover:bg-white/50 focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2 z-50"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              aria-controls={mobileMenuId}
            >
              {isMenuOpen ? (
                <X className="w-6 h-6 sm:w-7 sm:h-7 text-navy" aria-hidden="true" />
              ) : (
                <Menu className="w-6 h-6 sm:w-7 sm:h-7 text-navy" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </header>
      {/* Mobile Sliding Drawer */}
      <div
        className={`fixed inset-0 z-50 md:hidden transition-opacity duration-300 ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden={!isMenuOpen}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-navy/80 backdrop-blur-sm transition-opacity duration-300"
          onClick={() => setIsMenuOpen(false)}
          aria-hidden="true"
        />

        {/* Drawer */}
        <nav
          id={mobileMenuId}
          className={`absolute top-0 right-0 h-full w-72 sm:w-80 max-w-[85vw] bg-cream shadow-2xl transform transition-transform duration-300 ease-out ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          aria-label="Mobile navigation"
        >
          {/* Drawer Header */}
          <div className="flex items-center justify-between p-lg border-b border-slate/20">
            <h2 className="font-display text-lg sm:text-xl font-bold text-navy">
              Navigation
            </h2>
            <button
              type="button"
              onClick={() => setIsMenuOpen(false)}
              className="p-2 rounded-lg transition-all duration-300 hover:bg-navy/10 focus:outline-none focus:ring-2 focus:ring-teal"
              aria-label="Close menu"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6 text-navy" aria-hidden="true" />
            </button>
          </div>

          {/* Navigation Links */}
          <div className="flex flex-col p-md gap-sm">
            {navigationLinks?.map((link) => {
              const Icon = link?.icon;
              const isActive = isActivePath(link?.path);

              return (
                <Link
                  key={link?.path}
                  to={link?.path}
                  className={`flex items-center gap-md px-lg py-md rounded-xl transition-all duration-300 group ${
                    isActive
                      ? 'bg-teal text-white shadow-lg font-bold'
                      : 'hover:bg-white hover:shadow-md text-navy'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon
                    className={`w-5 h-5 sm:w-6 sm:h-6 transition-transform duration-300 group-hover:scale-110 ${
                      isActive ? 'text-white' : link?.color
                    }`}
                    aria-hidden="true"
                  />
                  <span className="font-body text-sm sm:text-base font-semibold">
                    {link?.label}
                  </span>
                </Link>
              );
            })}
          </div>

          {/* Drawer Footer */}
          <div className="absolute bottom-0 left-0 right-0 p-lg border-t border-slate/20">
            <div className="flex items-center gap-sm">
              <div className="w-2 h-2 rounded-full bg-sage animate-pulse" aria-hidden="true" />
              <p className="font-body text-xs sm:text-sm text-slate">
                The Poetic Method™
              </p>
            </div>
          </div>
        </nav>
      </div>
    </>
  );
};

export default Header;